/**
 * Feb 2026 — Canonical date/time formatter for the entire admin +
 * customer UI. Every timestamp shown to a user MUST render as
 *
 *     dd/mm/yyyy | hh:mm AM/PM     (e.g. 11/02/2026 | 06:45 PM)
 *
 * projected into **Asia/Muscat (+04:00)** regardless of the viewer's
 * browser timezone. The business runs on Muscat wall-clock time and
 * ops need one truth across every device, screen and printout.
 *
 * Accepts:
 *   - ISO 8601 string with an explicit timezone (e.g. `2026-07-19T19:22:04+00:00`
 *     or `...Z`) — parsed as-is.
 *   - Naive ISO 8601 string (no `Z`, no `±HH:MM`, no space-separated tz) —
 *     assumed to be UTC (that's what the backend writes) and a `Z` is
 *     appended before parsing.
 *   - Bare `YYYY-MM-DD` date — treated as midnight Muscat so the day
 *     stays put across timezones.
 *   - Date object or millis number — passed through.
 *
 * Bad/falsy input → "".
 */
export const MUSCAT_TZ = 'Asia/Muscat';

const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseAsUtc = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value !== 'string') return new Date(NaN);
  const trimmed = value.trim();
  if (!trimmed) return new Date(NaN);
  // Bare YYYY-MM-DD → treat as Muscat midnight (+04:00) to avoid the
  // browser silently applying UTC and drifting the day back.
  const bare = YMD_RE.exec(trimmed);
  if (bare) return new Date(`${bare[1]}-${bare[2]}-${bare[3]}T00:00:00+04:00`);
  // If it already carries a timezone marker or isn't a naive ISO shape
  // let the browser handle it.
  if (
    /[zZ]$/.test(trimmed) ||
    /[+-]\d{2}:?\d{2}$/.test(trimmed) ||
    !NAIVE_ISO_RE.test(trimmed)
  ) {
    return new Date(trimmed);
  }
  // Naive → assume UTC (that's what the backend writes for legacy rows).
  return new Date(`${trimmed}Z`);
};

/** Extract Muscat-projected calendar parts via `Intl.DateTimeFormat`.
 *  Returns null for invalid dates.  */
const partsInMuscat = (value) => {
  const d = parseAsUtc(value);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: MUSCAT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const out = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== 'literal') out[p.type] = p.value;
  }
  return out;
};

/**
 * Full timestamp: `dd/mm/yyyy | hh:mm AM/PM` in Asia/Muscat.
 */
export const formatDateTime = (value) => {
  if (!value) return '';
  const p = partsInMuscat(value);
  if (!p) return '';
  const period = String(p.dayPeriod || '').toUpperCase();
  return `${p.day}/${p.month}/${p.year} | ${p.hour}:${p.minute} ${period}`;
};

/**
 * Time-only: `hh:mm AM/PM` in Asia/Muscat.
 */
export const formatTime = (value) => {
  if (!value) return '';
  const p = partsInMuscat(value);
  if (!p) return '';
  const period = String(p.dayPeriod || '').toUpperCase();
  return `${p.hour}:${p.minute} ${period}`;
};

/**
 * Date-only: `dd/mm/yyyy` in Asia/Muscat.
 * Accepts bare `YYYY-MM-DD` and full ISO strings.
 */
export const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value.trim().match(YMD_RE);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const p = partsInMuscat(value);
  if (!p) return '';
  return `${p.day}/${p.month}/${p.year}`;
};
